import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { DialogModule } from 'primeng/dialog'
import { ButtonModule } from 'primeng/button'
import { SelectModule } from 'primeng/select'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { ChipModule } from 'primeng/chip'
import { InputTextModule } from 'primeng/inputtext'
import { EditorModule } from 'primeng/editor'
import { CheckboxModule } from 'primeng/checkbox'
import { FormsModule } from '@angular/forms'
import { StandardResponseService } from '../../services/standard-response.service'
import {
  MailContentModel,
  StandardResponseModel,
} from '../../../../core/models/standard-response.model'
import { catchError, finalize, of } from 'rxjs'
import { ToastService } from '../../../../shared/services/toast.service'

@Component({
  selector: 'app-send-mail-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    SelectModule,
    ProgressSpinnerModule,
    ChipModule,
    InputTextModule,
    EditorModule,
    CheckboxModule,
    FormsModule,
  ],
  templateUrl: './send-mail-modal.component.html',
  styleUrls: ['./send-mail-modal.component.scss'],
})
export class SendMailModalComponent implements OnChanges {
  @Input() visible: boolean = false
  @Input() interventionId: number | null = null
  @Input() affairRef: string | null = null
  @Input() erpSource: string | null = null

  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() onCancel = new EventEmitter<void>()
  @Output() onSent = new EventEmitter<void>()

  private standardResponseService = inject(StandardResponseService)
  private toastService = inject(ToastService)

  // Liste des réponses standardisées
  standardResponses: StandardResponseModel[] = []
  loadingStandardResponses: boolean = false

  // Réponse standardisée sélectionnée
  selectedStandardResponse: StandardResponseModel | null = null

  // Contenu du mail
  mailContent: MailContentModel | null = null
  loadingMailContent: boolean = false

  // État d'envoi
  sending: boolean = false

  // Contenu éditable
  editableContent: string = ''

  // Destinataires éditables
  editableRecipientsTo: string[] = []
  editableRecipientsCopy: string[] = []

  ngOnChanges(changes: SimpleChanges): void {
    // Charger les réponses standardisées quand la modale devient visible
    if (
      changes['visible'] &&
      changes['visible'].currentValue === true &&
      this.erpSource
    ) {
      this.loadStandardResponses()
    }

    // Réinitialiser quand la modale se ferme
    if (changes['visible'] && changes['visible'].currentValue === false) {
      this.resetForm()
    }
  }

  /**
   * Charge les réponses standardisées disponibles
   */
  loadStandardResponses(): void {
    if (!this.erpSource) return

    this.loadingStandardResponses = true
    this.standardResponseService
      .getForSelect(this.erpSource)
      .pipe(
        catchError((err) => {
          console.error(
            'Erreur lors du chargement des réponses standardisées',
            err
          )
          this.toastService.showError(
            'Impossible de charger les réponses standardisées'
          )
          return of([])
        }),
        finalize(() => (this.loadingStandardResponses = false))
      )
      .subscribe((responses) => {
        this.standardResponses = responses
      })
  }

  /**
   * Appelé quand une réponse standardisée est sélectionnée
   */
  onStandardResponseChange(): void {
    if (!this.selectedStandardResponse || !this.erpSource || !this.affairRef) {
      this.mailContent = null
      return
    }

    this.loadMailContent()
  }

  /**
   * Charge le contenu du mail pour la réponse standardisée sélectionnée
   */
  loadMailContent(): void {
    if (!this.selectedStandardResponse || !this.erpSource || !this.affairRef) {
      return
    }

    this.loadingMailContent = true
    this.standardResponseService
      .getMailContent(
        this.erpSource,
        this.affairRef,
        this.selectedStandardResponse.value,
        this.interventionId ?? undefined
      )
      .pipe(
        catchError((err) => {
          console.error('Erreur lors du chargement du contenu du mail', err)
          this.toastService.showError(
            'Impossible de charger le contenu du mail'
          )
          return of(null)
        }),
        finalize(() => (this.loadingMailContent = false))
      )
      .subscribe((content) => {
        this.mailContent = content
        // Initialiser les destinataires éditables
        if (content) {
          this.editableContent = content.content.content
          this.editableRecipientsTo = [...content.recipients.to]
          this.editableRecipientsCopy = [...content.recipients.copy]
        }
      })
  }

  /**
   * Ferme la modale
   */
  closeModal(): void {
    this.visible = false
    this.visibleChange.emit(false)
    this.onCancel.emit()
  }

  /**
   * Réinitialise le formulaire
   */
  resetForm(): void {
    this.selectedStandardResponse = null
    this.mailContent = null
    this.standardResponses = []
    this.sending = false
    this.editableContent = ''
    this.editableRecipientsTo = []
    this.editableRecipientsCopy = []
  }

  /**
   * Ajoute un destinataire à la liste "À"
   */
  addRecipientTo(event: Event): void {
    const input = event.target as HTMLInputElement
    const email = input.value.trim()
    if (
      email &&
      this.isValidEmail(email) &&
      !this.editableRecipientsTo.includes(email)
    ) {
      this.editableRecipientsTo.push(email)
      input.value = ''
    }
    event.preventDefault()
  }

  /**
   * Supprime un destinataire de la liste "À"
   */
  removeRecipientTo(index: number): void {
    this.editableRecipientsTo.splice(index, 1)
  }

  /**
   * Ajoute un destinataire à la liste "Cc"
   */
  addRecipientCopy(event: Event): void {
    const input = event.target as HTMLInputElement
    const email = input.value.trim()
    if (
      email &&
      this.isValidEmail(email) &&
      !this.editableRecipientsCopy.includes(email)
    ) {
      this.editableRecipientsCopy.push(email)
      input.value = ''
    }
    event.preventDefault()
  }

  /**
   * Supprime un destinataire de la liste "Cc"
   */
  removeRecipientCopy(index: number): void {
    this.editableRecipientsCopy.splice(index, 1)
  }

  /**
   * Valide le format d'un email
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Envoie le mail
   */
  sendMail(): void {
    if (
      !this.selectedStandardResponse ||
      !this.erpSource ||
      !this.affairRef ||
      !this.mailContent
    ) {
      return
    }

    // Vérifier qu'il y a des destinataires
    const allRecipients = [
      ...this.editableRecipientsTo,
      ...this.editableRecipientsCopy,
    ]
    if (allRecipients.length === 0) {
      this.toastService.showError('Aucun destinataire configuré')
      return
    }

    // Récupérer les paths des fichiers sélectionnés uniquement
    const allFilePaths = [
      ...this.mailContent.files.documentsCustomers,
      ...this.mailContent.files.documentsDelivered,
      ...this.mailContent.files.invoices,
      ...this.mailContent.files.quotations,
      ...this.mailContent.files.attachments,
    ]
      .filter((f) => f.isSelected)
      .map((f) => f.path)

    this.sending = true
    this.standardResponseService
      .sendMail({
        erpSource: this.erpSource,
        affairRef: this.affairRef,
        standardResponseId: this.selectedStandardResponse.value,
        interventionId: this.interventionId ?? undefined,
        subject: this.mailContent.content.subject,
        content: this.editableContent,
        recipients: allRecipients,
        files: allFilePaths,
      })
      .pipe(
        catchError((err) => {
          console.error("Erreur lors de l'envoi du mail", err)
          this.toastService.showError("Erreur lors de l'envoi du mail")
          return of({ status: 500, message: undefined })
        }),
        finalize(() => (this.sending = false))
      )
      .subscribe((result: any) => {
        if (result.status === 200) {
          this.toastService.showSuccess('Mail envoyé avec succès')
          this.onSent.emit()
          this.closeModal()
        } else if (result.message) {
          this.toastService.showError(result.message)
        }
      })
  }

  /**
   * Sélectionne/désélectionne tous les fichiers d'une catégorie
   */
  toggleAllFiles(files: { isSelected?: boolean }[], selected: boolean): void {
    files.forEach((f) => (f.isSelected = selected))
  }

  /**
   * Vérifie si tous les fichiers d'une catégorie sont sélectionnés
   */
  allSelected(files: { isSelected?: boolean }[]): boolean {
    return files.length > 0 && files.every((f) => f.isSelected)
  }

  /**
   * Gère la fermeture de la dialogue
   */
  onHide(): void {
    this.visibleChange.emit(false)
  }
}
